import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import type { StepWithSettings, Variable } from "@/domains/templates/types";
import {
  isJsonPointer,
  isOrdinaryVariable,
} from "@/domains/templates/subanalysis";
import {
  getStepVariableSources,
  getStepVariableBindingReadiness,
} from "@/domains/templates/step-variable-bindings";
import { templateBuilderQueries } from "@/features/template-builder/queries";
import { useSaveSubanalysis } from "../hooks/use-save-subanalysis";
import {
  invocationDefinition,
  replaceInvocationBinding,
  reconcileInvocationSource,
} from "../lib/invocation-variable-draft";
import {
  createBindingDraft,
  toSubanalysisDefinition,
  SubanalysisFieldError,
} from "../lib/subanalysis-draft";
import { getErrorMessage } from "@/shared/lib/error-message";
import { TemplateVariableReferenceField } from "./template-variable-reference-field";
import { SubanalysisBindingRow } from "./subanalysis-binding-row";

/** Mounted only while the standard variable dialog is open; selections are local. */
export function TemplateInvocationVariableFields({
  variable,
  variables,
  templateId,
  stepId,
  tenantId,
  onClose,
}: {
  variable: Variable;
  variables: Variable[];
  templateId: string;
  stepId: string;
  tenantId?: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [initial] = useState(() => invocationDefinition(variables));
  const [childId, setChildId] = useState(initial.call.binding_template_id);
  const [sourceStepId, setSourceStepId] = useState(
    initial.call.binding_source_step_id ?? "",
  );
  const [sourceId, setSourceId] = useState(
    initial.call.binding_source_variable_id ?? "",
  );
  const [path, setPath] = useState(initial.call.binding_path);
  const [binding, setBinding] = useState(() =>
    createBindingDraft(
      {
        ...variable,
        variable_id:
          variable.binding_target_variable_id ?? variable.variable_id,
      },
      initial.inputs.find(
        (input) =>
          input.binding_target_variable_id ===
          variable.binding_target_variable_id,
      ),
    ),
  );
  const revision = () =>
    queryClient
      .getQueryData<StepWithSettings[]>(
        templateBuilderQueries.steps(templateId).queryKey,
      )
      ?.find((step) => step.analysis_step_id === stepId)?.record_updated;
  const [expectedRevision, setExpectedRevision] = useState(revision);
  const save = useSaveSubanalysis(templateId, stepId, expectedRevision);
  const [error, setError] = useState<string>();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const steps = useQuery(templateBuilderQueries.steps(templateId));
  const list = useQuery({
    ...templateBuilderQueries.list({ showGlobal: true }, tenantId),
    enabled: variable.variable_role === "subanalysis_call" && !!tenantId,
  });
  const child = useQuery(templateBuilderQueries.detail(childId));
  const parent = useQuery(templateBuilderQueries.detail(templateId));
  const current = steps.data?.find((step) => step.id === stepId);
  const earlier = (steps.data ?? []).filter(
    (step) => current && step.order < current.order,
  );
  const groups = getStepVariableSources(
    steps.data ?? [],
    stepId,
    sourceStepId || "__none__",
    variable.variable_role === "subanalysis_source"
      ? "array"
      : variable.variable_type,
  );
  const arraySource = groups
    .flatMap((group) => [...group.inputs, ...group.outputs])
    .find((source) => source.id === sourceId);
  const targets = (child.data?.variables ?? []).filter(
    (target) =>
      isOrdinaryVariable(target) &&
      target.variable_kind === "input" &&
      target.analysis_step_id === null &&
      target.parent_variable_id === null,
  );
  const target = targets.find(
    (target) => target.variable_id === variable.binding_target_variable_id,
  );
  const parentVariables = (parent.data?.variables ?? []).filter(
    (v) =>
      isOrdinaryVariable(v) &&
      v.variable_kind === "input" &&
      v.parent_variable_id === null,
  );
  const changedChild = childId !== initial.call.binding_template_id;
  const changedSource =
    sourceStepId !== (initial.call.binding_source_step_id ?? "") ||
    sourceId !== (initial.call.binding_source_variable_id ?? "") ||
    path !== initial.call.binding_path;
  const disabled = save.isPending;
  const submit = async () => {
    setError(undefined);
    setFieldErrors({});
    let submitted = initial;
    try {
      if (!current) throw new Error("Wait for the section to load.");
      const latest = invocationDefinition(current.variables);
      submitted = latest;
      if (variable.variable_role === "subanalysis_call") {
        if (
          !list.data?.some(
            (item) => item.id === childId && item.id !== templateId,
          )
        )
          throw new Error("Select an accessible child template.");
        if (!child.data || child.isFetching)
          throw new Error("Wait for the child inputs to load.");
        submitted = {
          ...latest,
          call: { ...latest.call, binding_template_id: childId },
          inputs: targets.map((target) => {
            const existing = latest.inputs.find(
              (input) =>
                input.binding_target_variable_id === target.variable_id,
            );
            const previous = current.variables.find(
              (row) => row.binding_target_variable_id === target.variable_id,
            );
            return existing && previous?.variable_type === target.variable_type
              ? existing
              : {
                  binding_target_variable_id: target.variable_id,
                  binding_kind: "unbound" as const,
                };
          }),
        };
      } else if (variable.variable_role === "subanalysis_source") {
        if (sourceStepId && !earlier.some((step) => step.id === sourceStepId))
          throw new Error("Select an earlier section.");
        if (sourceId && !arraySource)
          throw new Error("Select an available array source.");
        if (!isJsonPointer(path))
          throw new Error("Array path must be a valid JSON Pointer.");
        const allowed = new Set(
          getStepVariableSources(
            steps.data ?? [],
            stepId,
            sourceStepId || "__none__",
          ).flatMap((group) =>
            [...group.inputs, ...group.outputs].map((source) => source.id),
          ),
        );
        submitted = reconcileInvocationSource(
          latest,
          sourceStepId,
          sourceId,
          path,
          allowed,
        );
      } else {
        const draft = {
          displayName: "",
          childTemplateId: latest.call.binding_template_id,
          sourceStepId: latest.call.binding_source_step_id ?? "",
          sourceVariableId: latest.call.binding_source_variable_id ?? "",
          arrayPath: latest.call.binding_path,
          bindings: [binding],
        };
        if (
          binding.kind === "step_variable" &&
          !groups
            .flatMap((group) => [...group.inputs, ...group.outputs])
            .some((source) => source.id === binding.sourceVariableId)
        )
          throw new Error("Select an available earlier-section variable.");
        if (
          binding.kind === "parent_variable" &&
          !parentVariables.some(
            (v) => v.variable_id === binding.sourceVariableId,
          )
        )
          throw new Error("Select an available parent variable.");
        submitted = replaceInvocationBinding(
          latest,
          toSubanalysisDefinition(draft).inputs[0]!,
        );
      }
      await save.mutateAsync(submitted);
      onClose();
    } catch (caught) {
      setError(getErrorMessage(caught, "Could not save variable"));
      const index = submitted.inputs.findIndex(
        (input) =>
          input.binding_target_variable_id ===
          variable.binding_target_variable_id,
      );
      const details =
        caught && typeof caught === "object" && "details" in caught
          ? caught.details
          : undefined;
      const errors =
        caught instanceof SubanalysisFieldError
          ? { [caught.path]: caught.message }
          : details && typeof details === "object" && "fieldErrors" in details
            ? details.fieldErrors
            : undefined;
      if (errors && typeof errors === "object")
        setFieldErrors(
          Object.fromEntries(
            Object.entries(errors)
              .filter(
                (entry): entry is [string, string] =>
                  typeof entry[1] === "string",
              )
              .map(([key, value]) => [
                key
                  .replace(/\[(\d+)\]/g, ".$1")
                  .replace(
                    `inputs.${caught instanceof SubanalysisFieldError ? 0 : index}.`,
                    "",
                  ),
                value,
              ]),
          ),
        );
      // Only a confirmed conflict authorizes acquiring the refreshed revision.
      // useSaveSubanalysis awaits canonical refresh before rejecting a 409.
      if (
        caught &&
        typeof caught === "object" &&
        "status" in caught &&
        caught.status === 409
      ) {
        setExpectedRevision(revision());
      }
    }
  };
  return (
    <div className="space-y-4">
      {variable.variable_role === "subanalysis_call" ? (
        <TemplateVariableReferenceField
          label="Child template"
          value={childId}
          options={(list.data ?? [])
            .filter((item) => item.id !== templateId)
            .map((item) => ({ id: item.id, name: item.name }))}
          onChange={setChildId}
          disabled={disabled || list.isLoading}
        />
      ) : variable.variable_role === "subanalysis_source" ? (
        <>
          <TemplateVariableReferenceField
            label="Earlier section"
            value={sourceStepId}
            options={earlier.map((step) => ({ id: step.id, name: step.name }))}
            onChange={(id) => {
              setSourceStepId(id);
              setSourceId("");
              setPath("");
            }}
            disabled={disabled}
          />
          <div className="space-y-3 border-l pl-4">
            <TemplateVariableReferenceField
              label="Source variable"
              value={sourceId}
              options={groups.flatMap((group) =>
                [...group.inputs, ...group.outputs].map((source) => ({
                  id: source.id,
                  name: `${group.stepName} → ${source.direction === "input" ? "Input" : "Output"} → ${source.variable.variable_name_frontend || source.variable.variable_name} (${source.type})`,
                })),
              )}
              onChange={(id) => {
                setSourceId(id);
                setPath("");
              }}
              disabled={disabled || !sourceStepId}
            />
            <Label htmlFor="array-path">
              Optional array path (JSON Pointer)
            </Label>
            <Input
              id="array-path"
              value={path}
              onChange={(event) => setPath(event.target.value)}
              disabled={disabled}
            />
            {!arraySource ||
            getStepVariableBindingReadiness(arraySource, path, "array") ===
              "incomplete" ? (
              <p role="status">
                Incomplete: select a typed array source. Dynamic projections
                require a declared array output.
              </p>
            ) : null}
          </div>
        </>
      ) : (
        <SubanalysisBindingRow
          row={{ ...binding, target: target ?? binding.target }}
          parentVariables={parentVariables}
          sourceGroups={groups}
          onChange={setBinding}
          disabled={disabled}
          errors={fieldErrors}
        />
      )}
      {(changedChild || changedSource) && (
        <div role="status" className="rounded-md border p-3 text-sm">
          <p>
            Affected bindings:{" "}
            {variables
              .filter((v) => v.variable_role === "bound_input")
              .map((v) => v.variable_name_frontend || v.variable_name)
              .join(", ") || "None"}
            .
          </p>
          <p>
            {changedChild
              ? "Compatible target IDs are preserved; removed targets are replaced and new inputs become unbound on Save."
              : "Item bindings and unavailable earlier-variable references become unbound on Save. Other bindings are preserved."}{" "}
            Save confirms this change; Cancel keeps the current selections.
          </p>
        </div>
      )}
      {Object.entries(fieldErrors)
        .filter(([key]) => key.startsWith("call."))
        .map(([key, message]) => (
          <p key={key} role="alert">
            {message}
          </p>
        ))}
      {(error || steps.error || child.error || list.error || parent.error) && (
        <p role="alert" className="text-sm text-destructive">
          {error ??
            getErrorMessage(
              steps.error ?? child.error ?? list.error ?? parent.error,
              "Could not load references",
            )}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} disabled={disabled}>
          Cancel
        </Button>
        <Button
          onClick={() => void submit()}
          disabled={
            disabled ||
            steps.isLoading ||
            child.isLoading ||
            (variable.variable_role === "subanalysis_call" && child.isFetching)
          }
        >
          {disabled ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}
